import { useEffect, useMemo } from "react";
import { SerializeFrom } from "@remix-run/node";
import clsx from "clsx";
import { AppointmentStateEnum } from "skedify-types";

import { AppointmentInstructions } from "./Appointment/AppointmentInstructions";
import { AppointmentSummary } from "./Appointment/AppointmentSummary";
import { CalendarLinks } from "./Appointment/CalendarLinks";
import { CustomerSection } from "./Appointment/CustomerSection";
import { QuestionSection } from "./Appointment/QuestionSection";
import { Section } from "./Appointment/Section";
import { Markdown } from "./Markdown";
import { PageTitle } from "./PageTitle";

import { Text } from "~/components/Text";
import { sendMessage } from "~/hooks/useSendMessage";
import { useStateManager } from "~/modules/StateManager.hooks";
import type { AppointmentDetails } from "~/utils/appointment.utils.server";

interface ConfirmationProps {
  appointment: SerializeFrom<AppointmentDetails>;
  type: "APPOINTMENT_CREATED" | "INVITE_ACCEPTED" | "APPOINTMENT_EDITED";
}

export function Confirmation({ appointment, type }: ConfirmationProps) {
  const stateManager = useStateManager();

  const showAnswersLeft =
    appointment.subject.instructions ||
    (appointment.meetingType === "office" &&
      (appointment.office.directions || appointment.office.parkingInfo));

  const subjectCategoryId = appointment.subject.category_id;

  useEffect(() => {
    sendMessage({
      type: "STATE_UPDATE",
      payload: {
        ...stateManager.state,
        subjectCategoryId,
      },
    });
    sendMessage({ type });
  }, [stateManager.state, subjectCategoryId, type]);

  const title = useMemo(() => {
    if (appointment.state === AppointmentStateEnum.ACCEPTED) {
      return appointment.identity === "contact" ? (
        <Text i18nKey="Confirmation.title.contact.accepted" />
      ) : (
        <Text i18nKey="Confirmation.title.accepted" />
      );
    }
    if (appointment.state === AppointmentStateEnum.INCOMING) {
      return appointment.identity === "contact" ? (
        <Text i18nKey="Confirmation.title.contact.incoming" />
      ) : (
        <Text i18nKey="Confirmation.title.incoming" />
      );
    }
    if (appointment.state === AppointmentStateEnum.OUTGOING) {
      return appointment.identity === "contact" ? (
        <Text i18nKey="Confirmation.title.contact.accepted" />
      ) : (
        <Text i18nKey="Confirmation.title.outgoing" />
      );
    }
  }, [appointment.state, appointment.identity]);

  const subtitle = useMemo(() => {
    if (appointment.identity === "contact") return null;

    if (appointment.state === AppointmentStateEnum.ACCEPTED) {
      return <Text i18nKey="Confirmation.subtitle.accepted" />;
    }
    if (appointment.state === AppointmentStateEnum.INCOMING) {
      return <Text i18nKey="Confirmation.subtitle.incoming" />;
    }
    if (appointment.state === AppointmentStateEnum.OUTGOING) {
      return <Text i18nKey="Confirmation.subtitle.outgoing" />;
    }
  }, [appointment.state, appointment.identity]);

  return (
    <div>
      <PageTitle data-variant="Cancel" subtitle={subtitle}>
        {title}
      </PageTitle>
      <section
        className={clsx(
          "grid grid-cols-1 items-center gap-x-lg gap-y-md",
          "md:grid-cols-2 md:gap-x-lg",
          "lg:grid-cols-2 lg:gap-x-xl",
          "xl:grid-cols-3 xl:gap-x-2xl",
        )}
        data-meeting-type={appointment.meetingType}
        data-variant="Confirmation"
      >
        <div className="grid gap-sm">
          <AppointmentSummary
            employee={appointment.employee}
            location={appointment.location}
            meetingType={appointment.meetingType}
            office={appointment.office}
            possibility={appointment.possibility}
            subject={appointment.subject}
          />
          {appointment.calendarLinks && (
            <div className="inline-flex w-full justify-center">
              <CalendarLinks calendarLinks={appointment.calendarLinks} />
            </div>
          )}
        </div>
        <div
          className="grid lg:grid-cols-2 lg:gap-lg xl:col-span-2 xl:divide-x"
          data-component="InfoSection"
        >
          <div className="grid gap-sm">
            <CustomerSection customer={appointment.customer} />
            {showAnswersLeft && (
              <QuestionSection
                answers={appointment.answers}
                questions={appointment.questionFields}
              />
            )}
          </div>

          <div className="grid gap-sm self-start xl:pl-md">
            {!showAnswersLeft && (
              <QuestionSection
                answers={appointment.answers}
                questions={appointment.questionFields}
              />
            )}
            <AppointmentInstructions subject={appointment.subject} />
            {appointment.meetingType === "office" && (
              <>
                <OfficeDirections office={appointment.office} />
                <OfficeParkingInfo office={appointment.office} />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function OfficeDirections({ office }: Pick<AppointmentDetails, "office">) {
  if (!office.directions) return null;

  return (
    <Section
      data-variant="OfficeDirections"
      title={<Text i18nKey="Confirmation.office.directions" />}
    >
      <Section.Item>
        <Markdown text={office.directions} />
      </Section.Item>
    </Section>
  );
}

function OfficeParkingInfo({ office }: Pick<AppointmentDetails, "office">) {
  if (!office.parkingInfo) return null;

  return (
    <Section
      data-variant="OfficeParkingInfo"
      title={<Text i18nKey="Confirmation.office.parking" />}
    >
      <Section.Item>
        <Markdown text={office.parkingInfo} />
      </Section.Item>
    </Section>
  );
}
